// UI Floating Dropdown Controller - Proper Floating UI implementation
// Responsibilities: dropdown state, Floating UI positioning, accessibility, cleanup
// CLAUDE.md compliance: follows floating_ui_rules with proper Tailwind integration
//
import { Controller } from "@hotwired/stimulus"
import { computePosition, flip, shift, offset } from "@floating-ui/dom"

export default class extends Controller {
  // Singleton pattern - only one dropdown can be open at a time
  static openInstance = null
  
  static targets = ["button", "menu", "chevron"]
  static values = {
    placement: { type: String, default: "bottom-end" },
    strategy: { type: String, default: "fixed" },
    offset: { type: Number, default: 8 },
    sameWidth: { type: Boolean, default: false }
  }

  connect() {
    console.log('[DEBUG] FloatingDropdown controller connected')
    this.initializeState()
    this.setupEventHandlers()
    this.isOpen = false
    
    // Bind event handlers for proper cleanup
    this.boundClickOutside = this.closeOnClickOutside.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    
    // Add global listeners
    document.addEventListener('click', this.boundClickOutside)
    document.addEventListener('keydown', this.boundKeydown)
    
    // Ensure menu is properly positioned from the start
    if (this.hasMenuTarget) {
      this.initializeMenuPosition()
    }
    
    console.log('[DEBUG] Controller initialized, targets found:', {
      button: this.hasButtonTarget,
      menu: this.hasMenuTarget,
      element: this.element
    })
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    console.log('[DEBUG] Toggle clicked, current state:', this.isOpen)
    this.isOpen ? this.close() : this.open()
  }

  async open() {
    console.log('[DEBUG] Opening dropdown')
    
    // Singleton pattern: close any other open dropdown first
    if (this.constructor.openInstance && this.constructor.openInstance !== this) {
      console.log('[DEBUG] Closing previously open dropdown')
      this.constructor.openInstance.close()
    }
    
    this.isOpen = true
    this.constructor.openInstance = this
    
    // Show menu - proper Floating UI approach with Tailwind classes
    this.menuTarget.classList.remove("invisible", "opacity-0", "scale-95", "pointer-events-none")
    this.menuTarget.classList.add("visible", "opacity-100", "scale-100")
    
    // Floating UI positioning (according to CLAUDE.md rules)
    await this.updatePosition()
    
    // Update accessibility
    this.updateAriaExpanded(true)
    
    // Rotate chevron icon if present
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.add('rotate-180')
    }
    
    console.log('[DEBUG] Dropdown opened, classes:', this.menuTarget.className)
  }

  close() {
    console.log('[DEBUG] Closing dropdown')
    this.isOpen = false
    
    // Clear singleton instance if this is the open instance
    if (this.constructor.openInstance === this) {
      this.constructor.openInstance = null
    }
    
    // Hide menu - proper Floating UI approach with Tailwind classes
    this.menuTarget.classList.remove("visible", "opacity-100", "scale-100")
    this.menuTarget.classList.add("invisible", "opacity-0", "scale-95", "pointer-events-none")
    
    // Update accessibility
    this.updateAriaExpanded(false)
    
    // Rotate chevron icon back if present
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.remove('rotate-180')
    }
    
    console.log('[DEBUG] Dropdown closed, classes:', this.menuTarget.className)
  }

  closeOnClickOutside(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  handleKeydown(event) {
    // Accessibility: keyboard navigation
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault()
      this.close()
      this.buttonTarget.focus()
    }
  }

  disconnect() {
    // Close if this instance is currently open
    if (this.isOpen) {
      this.close()
    }
    
    // Clean up singleton state if this was the open instance
    if (this.constructor.openInstance === this) {
      this.constructor.openInstance = null
    }
    
    // Remove global event listeners
    document.removeEventListener('click', this.boundClickOutside)
    document.removeEventListener('keydown', this.boundKeydown)
    
    this.cleanup()
    console.log('[DEBUG] Controller disconnected')
  }

  turboBeforeCache = () => {
    if (this.isOpen) {
      this.close()
    }
  }

  // ===== INITIALIZATION =====
  initializeState() {
    this.startTime = Date.now()
    
    // Add turbo listener for cleanup
    document.addEventListener('turbo:before-cache', this.turboBeforeCache)
  }

  initializeMenuPosition() {
    // Ensure menu is absolutely positioned and doesn't affect layout
    if (this.hasMenuTarget) {
      Object.assign(this.menuTarget.style, {
        position: this.strategyValue, // 'fixed' by default
        zIndex: '50', // CLAUDE.md z-index for dropdowns
        left: '0px', // Will be updated by Floating UI
        top: '0px'   // Will be updated by Floating UI
      })
      console.log('[DEBUG] Menu position initialized with strategy:', this.strategyValue)
    }
  }

  setupEventHandlers() {
    // Event handlers are now set up in connect() method
    // This method kept for compatibility but no longer needed
  }

  // ===== ACCESSIBILITY =====
  updateAriaExpanded(isExpanded) {
    const button = this.hasButtonTarget ? this.buttonTarget : 
      this.element.querySelector('[data-action*="ui--floating-dropdown#toggle"]')
    
    if (button) {
      button.setAttribute('aria-expanded', isExpanded.toString())
      button.setAttribute('aria-haspopup', 'menu')
    }
    
    if (this.hasMenuTarget) {
      this.menuTarget.setAttribute('role', 'menu')
    }
  }

  // ===== CLEANUP =====
  cleanup() {
    if (this.turboBeforeCache) {
      document.removeEventListener('turbo:before-cache', this.turboBeforeCache)
    }
    // Note: global listeners are now cleaned up in disconnect() method
  }

  // ===== UTILITIES =====
  getUptime() {
    return Date.now() - this.startTime
  }

  // ===== FLOATING UI POSITIONING =====
  async updatePosition() {
    // CLAUDE.md compliance: proper Floating UI with mandatory middleware
    const { x, y } = await computePosition(this.buttonTarget, this.menuTarget, {
      placement: this.placementValue,
      strategy: this.strategyValue,
      middleware: [
        offset(this.offsetValue),
        flip(),
        shift({ padding: 8 })
      ]
    })

    // Ensure menu is absolutely positioned and doesn't affect layout
    Object.assign(this.menuTarget.style, {
      position: this.strategyValue, // 'fixed' by default
      left: `${x}px`,
      top: `${y}px`,
      zIndex: '50' // CLAUDE.md z-index for dropdowns
    })

    console.log('[DEBUG] Floating UI positioned at:', { x, y, placement: this.placementValue, strategy: this.strategyValue })
  }

  // ===== A+ HYBRID ENHANCEMENTS - Model Bindings Traffic Split =====
  updateTrafficSplit(event) {
    const input = event.target
    const bindingId = input.dataset.bindingId
    const value = input.value
    
    // Update display value
    const valueDisplay = document.getElementById(`traffic-value-${bindingId}`)
    if (valueDisplay) {
      valueDisplay.textContent = `${value}%`
    }

    console.log('[DEBUG] Traffic split updated:', { bindingId, value })
  }

  saveTrafficSplit(event) {
    event.preventDefault()
    const button = event.target
    const bindingId = button.dataset.bindingId
    const input = this.menuTarget.querySelector('input[type="range"]')
    
    if (!input) return

    const value = input.value
    
    // Send PATCH request to update traffic split
    fetch(`/admin/ai_providers/model_bindings/${bindingId}/update_traffic_split`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({ traffic_percentage: value })
    })
    .then(response => {
      if (response.ok) {
        // Update progress bar in table
        const progressBar = document.querySelector(`tr:has([data-binding-id="${bindingId}"]) .bg-blue-600`)
        if (progressBar) {
          progressBar.style.width = `${value}%`
        }
        
        // Update percentage display in table
        const percentageDisplay = progressBar?.parentElement.nextElementSibling
        if (percentageDisplay) {
          percentageDisplay.textContent = `${value}%`
        }
        
        this.close()
        
        // Show success message
        this.showNotification('Traffic split updated successfully', 'success')
      } else {
        throw new Error('Failed to update traffic split')
      }
    })
    .catch(error => {
      console.error('[ERROR] Failed to save traffic split:', error)
      this.showNotification('Failed to update traffic split', 'error')
    })
  }

  // ===== NOTIFICATIONS =====
  showNotification(message, type = 'info') {
    // Simple notification implementation
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-white ${type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600'}`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}